import React, { useState } from 'react';
import styled from 'styled-components';
import { RefreshCw, SearchX, Info } from 'lucide-react';
import { backofficeApi, formatNumber } from '@kardume/services';
import type { SearchTermUsageDto } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  ActionButton,
  Select,
  TextInput,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: flex-end;

  select {
    box-sizing: border-box;
    height: 42px;
  }
`;

const FilterField = styled.div`
  flex: 1 1 180px;
  min-width: 160px;
`;

const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0 0 1.25rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};

  strong {
    color: ${(p) => p.theme.textSecondary};
  }
`;

const TermCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const SCOPE_OPTIONS = [
  { value: 'zero', label: 'Só os que não acharam nada' },
  { value: 'all', label: 'Todos os termos buscados' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * `2026-08-30` -> `30/08/2026`, sem passar por `Date`.
 *
 * O formatador compartilhado de datas faz `new Date(iso)`, e uma string
 * só-data é interpretada como meia-noite **UTC**: num navegador em UTC-3 o dia
 * volta um dia para trás. Aqui o dado é um dia de calendário do fuso do Kardume
 * (`America/Sao_Paulo`, o mesmo em que a linha foi gravada) e não um instante -
 * então a conversão certa é nenhuma.
 */
const formatDay = (iso: string): string => {
  const [ano, mes, dia] = iso.split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
};

/**
 * O que as pessoas procuram e o Kardume não tem (card 346).
 *
 * O card #186 chama esta lista de "a lista de prospecção comercial mais honesta
 * que existe", e o motivo é que ninguém opina nela: o termo veio de quem
 * digitou na busca da vitrine. Fica em Comercial, ao lado de "Interesse por
 * empresa", porque é a mesma etapa do funil vista pelo outro lado - lá, onde há
 * procura por quem já está no guia; aqui, o que procuram e o guia não tem.
 *
 * A tela diz **o que ela está escondendo**: o piso de frequência e o período
 * vêm do servidor (`minCount`, `from`, `to`) e são escritos no aviso, em vez de
 * copiados aqui - com o número copiado, o dia em que o piso mudasse a tela
 * mentiria.
 */
const SearchTermsPage: React.FC = () => {
  const [localityId, setLocalityId] = useState<string>('');
  const [scope, setScope] = useState<string>('zero');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const localities = useAsync(
    () => backofficeApi.listLocalities({ page: 0, size: 200, active: true, sort: 'name,asc' }),
    []
  );

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.getSearchTerms({
        page,
        size: pageSize,
        localityId: localityId || undefined,
        from: from || undefined,
        to: to || undefined,
        onlyZeroResults: scope === 'zero',
      }),
    [page, pageSize, localityId, from, to, scope]
  );

  const terms: SearchTermUsageDto[] = data?.page.content ?? [];
  const totalItems = data?.page.totalElements ?? 0;
  const totalPages = data?.page.totalPages ?? 0;

  const changeFilter = (apply: () => void): void => {
    apply();
    setPage(0);
  };

  const localityOptions = [
    { value: '', label: 'Todas as cidades' },
    ...(localities.data?.content ?? []).map((l) => ({ value: l.id, label: `${l.name}/${l.state}` })),
  ];

  /** Buscas somadas da página - o volume que a fila representa. */
  const somaBuscas = terms.reduce((total, t) => total + t.searchCount, 0);
  const somaSemResultado = terms.reduce((total, t) => total + t.zeroResultCount, 0);

  return (
    <>
      <PageHeader
        title="Termos buscados"
        subtitle="O que as pessoas digitaram na busca da vitrine e não encontraram. É a pergunta comercial mais direta que existe: que categoria falta nesta cidade."
      />

      <StatsGrid>
        <StatCard label="Termos distintos (no recorte)" value={formatNumber(totalItems)} />
        <StatCard label="Buscas (nesta página)" value={formatNumber(somaBuscas)} />
        <StatCard label="Sem resultado (nesta página)" value={formatNumber(somaSemResultado)} />
      </StatsGrid>

      <Toolbar>
        <FilterField>
          <Select
            label="Cidade"
            options={localityOptions}
            value={localityId}
            onChange={(e) => changeFilter(() => setLocalityId(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <Select
            label="Mostrar"
            options={SCOPE_OPTIONS}
            value={scope}
            onChange={(e) => changeFilter(() => setScope(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <TextInput
            label="De"
            type="date"
            value={from}
            onChange={(e) => changeFilter(() => setFrom(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <TextInput
            label="Até"
            type="date"
            value={to}
            onChange={(e) => changeFilter(() => setTo(e.target.value))}
          />
        </FilterField>
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      {data && (
        <Note>
          <Info size={16} />
          <span>
            Período: <strong>{formatDay(data.from)}</strong> a <strong>{formatDay(data.to)}</strong> - sem
            datas informadas, os últimos 30 dias. Termos buscados menos de{' '}
            <strong>{data.minCount} vezes</strong> no período não são listados: só se guarda a contagem
            do termo, sem nada que diga quem buscou, e o termo procurado uma única vez não indica
            demanda nenhuma. Contamos <strong>buscas</strong>, não pessoas.
          </span>
        </Note>
      )}

      <DataState
        loading={loading}
        error={error}
        isEmpty={terms.length === 0}
        emptyTitle="Nenhum termo neste recorte"
        emptyDescription="Ninguém buscou o suficiente neste período e nesta cidade para a lista aparecer. Amplie o período ou escolha outra cidade."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Termo</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Sem resultado</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Buscas</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Última busca</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((item) => (
                <tr key={item.term}>
                  <td>
                    <TermCell>
                      <SearchX size={14} />
                      <strong>{item.term}</strong>
                      {item.zeroResultCount > 0 && item.zeroResultCount < item.searchCount && (
                        <Badge tone="warning">Passou a achar</Badge>
                      )}
                    </TermCell>
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(item.zeroResultCount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(item.searchCount)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDay(item.lastDay)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}
    </>
  );
};

export default SearchTermsPage;
